// اجزای Header پوسته کارکنان: انتخاب شعبه فعال، منوی حساب (جابه‌جایی مستأجر، خروج)، اعلان ارتقا و محافظ نشست مستأجر.
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CheckIcon from '@mui/icons-material/Check';
import LogoutIcon from '@mui/icons-material/Logout';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import { useTranslation } from '@phoenix/i18n';
import { LoadingState } from '@phoenix/ui';
import { Fragment, useId, useState, type ReactNode } from 'react';
import { Link as RouterLink, Navigate, useLocation } from 'react-router';
import { useAuth } from './AuthProvider';
import { useTenantName } from './TenantSelectPage';
import { useOptionalTenantSession, useTenantSession } from './TenantSessionProvider';

/** شعبه فعال در Header؛ تغییر آن بافت عملیات ثبتی را تغییر می‌دهد (AC-IAM-002.2). */
export function BranchSelector() {
  const { t } = useTranslation('shell');
  const { activeTenant, session, switchBranch } = useTenantSession();
  const branches = activeTenant?.branches ?? [];
  if (!session || branches.length === 0) {
    return null;
  }
  const value = session.activeBranchId ?? '';
  return (
    <TextField
      select
      size="small"
      value={value}
      onChange={(event) => void switchBranch(event.target.value)}
      label={t('shell:header.branch_label')}
      sx={{ minWidth: 180, maxWidth: 240 }}
      fullWidth={false}
      error={!value && branches.length > 1}
      slotProps={{
        htmlInput: { 'data-testid': 'branch-selector' },
        select: { displayEmpty: true },
        inputLabel: { shrink: true },
      }}
    >
      {!value && (
        <MenuItem value="" disabled>
          {t('shell:header.branch_choose')}
        </MenuItem>
      )}
      {branches.map((branch) => (
        <MenuItem key={branch.branchId} value={branch.branchId}>
          {branch.name || t('shell:branch.unnamed')}
        </MenuItem>
      ))}
    </TextField>
  );
}

export interface AccountMenuProps {
  /** اقلام اضافه (مثل حساب من)؛ تابع ← بستن منو پس از انتخاب. */
  extraItems?: ReactNode | ((close: () => void) => ReactNode);
  /** نمایش بخش جابه‌جایی مستأجر (staff). */
  showTenants?: boolean;
}

/** منوی حساب: نام کاربر، جابه‌جایی مستأجر (AC-IAM-002.3) و خروج. */
export function AccountMenu({ extraItems, showTenants = true }: AccountMenuProps) {
  const { t } = useTranslation('shell');
  const auth = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const optionalSession = useOptionalTenantSession();
  const tenantSession = showTenants ? optionalSession : null;
  const tenantName = useTenantName();
  const close = () => setAnchor(null);

  return (
    <>
      <Button
        color="inherit"
        startIcon={<AccountCircleOutlinedIcon />}
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-controls={anchor ? menuId : undefined}
        aria-expanded={anchor ? true : undefined}
        data-testid="account-menu"
      >
        {auth.user?.name ?? auth.user?.username ?? t('shell:header.account')}
      </Button>
      <Menu id={menuId} anchorEl={anchor} open={anchor !== null} onClose={close}>
        {typeof extraItems === 'function' ? extraItems(close) : extraItems}
        {tenantSession &&
          tenantSession.tenants.length > 0 && [
            <ListSubheader key="tenants-header">{t('shell:header.switch_tenant')}</ListSubheader>,
            ...tenantSession.tenants.map((tenant) => {
              const current = tenant.tenantId === tenantSession.session?.tenantId;
              return (
                <MenuItem
                  key={tenant.tenantId}
                  selected={current}
                  data-testid={`switch-tenant-${tenant.tenantId}`}
                  onClick={() => {
                    close();
                    if (!current) {
                      void tenantSession.switchTenant(tenant.tenantId);
                    }
                  }}
                >
                  <ListItemIcon>
                    {current ? <CheckIcon fontSize="small" /> : <SwapHorizIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText primary={tenantName(tenant)} />
                </MenuItem>
              );
            }),
            <Divider key="tenants-divider" />,
          ]}
        <MenuItem
          onClick={() => {
            close();
            void auth.logout();
          }}
          data-testid="logout"
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('auth:account.logout')} />
        </MenuItem>
      </Menu>
    </>
  );
}

/** اعلان 403 PHX-PLT-0403 (Feature غیرفعال) با لینک ارتقا (AC-PLT-031.2). */
export function FeatureDisabledNotice({ upgradePath }: { upgradePath: string }) {
  const { t } = useTranslation('shell');
  const { notices, dismissFeatureNotice } = useTenantSession();
  const error = notices.featureDisabled;
  return (
    <Snackbar
      open={error !== null}
      onClose={dismissFeatureNotice}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity="info"
        onClose={dismissFeatureNotice}
        data-testid="feature-disabled-notice"
        data-feature={error?.feature}
        action={
          <Button component={RouterLink} to={upgradePath} color="inherit" onClick={dismissFeatureNotice}>
            {t('common:actions.upgrade')}
          </Button>
        }
      >
        {t('errors:error.feature_disabled')}
      </Alert>
    </Snackbar>
  );
}

/** Routeهای پوسته فقط با نشست مستأجر فعال؛ در غیر این صورت صفحه انتخاب مستأجر (SCR-STAFF-001). */
export function RequireTenantSession({ children }: { children: ReactNode }) {
  const { status, epoch } = useTenantSession();
  const location = useLocation();
  if (status === 'active' || status === 'suspended') {
    // کلید epoch: جابه‌جایی مستأجر کل درخت (فرم‌ها و وضعیت محلی) را از نو می‌سازد (AC-IAM-002.3).
    return <Fragment key={epoch}>{children}</Fragment>;
  }
  if (status === 'loading' || status === 'selecting') {
    return <LoadingState />;
  }
  return <Navigate to="/select-tenant" replace state={{ from: location.pathname }} />;
}
